/**
 * Utility functions for user-friendly error message handling.
 *
 * @remarks
 * This module provides functions to convert technical error messages into
 * user-friendly messages and generate correlation IDs for error tracking.
 *
 * @see Issue #184 - Enhancement: Error message handling
 */

/**
 * Converts technical error messages into user-friendly messages
 * @param error - The error object or string
 * @returns A user-friendly error message
 */
export const getUserFriendlyMessage = (error: unknown): string => {
  // Handle string errors
  if (typeof error === 'string') {
    return mapErrorMessage(error);
  }

  // Handle Error objects
  if (error instanceof Error) {
    return mapErrorMessage(error.message);
  }

  // Handle objects with message property
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return mapErrorMessage((error as { message: string }).message);
  }

  // Default fallback
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Maps technical error messages to user-friendly messages
 */
const mapErrorMessage = (message: string): string => {
  const lowerMessage = message.toLowerCase();

  // Network errors
  if (
    lowerMessage.includes('network') ||
    lowerMessage.includes('fetch failed') ||
    lowerMessage.includes('failed to fetch') ||
    lowerMessage.includes('net::err')
  ) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }

  // Authentication errors
  if (lowerMessage.includes('401') || lowerMessage.includes('unauthorized')) {
    return 'Your session has expired. Please log in again.';
  }

  // Authorization errors
  if (lowerMessage.includes('403') || lowerMessage.includes('forbidden')) {
    return 'You do not have permission to perform this action. Please contact your administrator.';
  }

  // Not found errors
  if (lowerMessage.includes('404') || lowerMessage.includes('not found')) {
    return 'The requested resource was not found. Please verify the data exists.';
  }

  // Bad request errors (400) - preserve the server's validation message
  // These contain specific validation errors that are user-friendly
  if (lowerMessage.includes('400') || lowerMessage.includes('bad request')) {
    // Extract the actual error message from the response
    // Format is typically "Request failed with 400: {error message}" or similar
    // Note: First regex already handles "request failed with 400" prefix
    const cleanedMessage = message
      .replace(/^.*?400[:\s]*/i, '')
      .replace(/bad request[:\s]*/i, '')
      .replace(/InputError[:\s]*/i, '')
      .trim();
    return (
      cleanedMessage ||
      'The request was invalid. Please check your input and try again.'
    );
  }

  // Conflict errors (409) - preserve the server's message as it contains important context
  if (lowerMessage.includes('409') || lowerMessage.includes('conflict')) {
    // Extract the actual error message from the response if available
    // ConflictError messages are user-friendly and should be shown
    const cleanedMessage = message
      .replace(/^.*?409[:\s]*/i, '')
      .replace(/ConflictError:\s*/i, '')
      .trim();
    return (
      cleanedMessage ||
      'This operation cannot be completed due to a conflict. Please check the current state and try again.'
    );
  }

  // Validation errors
  if (lowerMessage.includes('validation') || lowerMessage.includes('invalid')) {
    return 'The provided data is invalid. Please check your input and try again.';
  }

  // Server errors
  if (
    lowerMessage.includes('500') ||
    lowerMessage.includes('internal server error') ||
    lowerMessage.includes('server error')
  ) {
    return 'A server error occurred. Please try again later or contact support.';
  }

  // Timeout errors
  if (lowerMessage.includes('timeout') || lowerMessage.includes('timed out')) {
    return 'The request took too long to complete. Please try again.';
  }

  // Rate limiting
  if (
    lowerMessage.includes('429') ||
    lowerMessage.includes('rate limit') ||
    lowerMessage.includes('too many requests')
  ) {
    return 'Too many requests. Please wait a moment and try again.';
  }

  // Configuration errors
  if (
    lowerMessage.includes('configuration') ||
    lowerMessage.includes('config')
  ) {
    return 'There was a configuration issue. Please contact support.';
  }

  // Return original message if no mapping found (but clean up technical details)
  // Remove stack traces and technical prefixes
  const cleanMessage = message
    .replace(/Error:\s*/gi, '')
    .replace(/\s*at\s+.*$/gm, '')
    .trim();

  // If the message is still technical, return generic message
  if (cleanMessage.length > 200 || cleanMessage.includes('\\n')) {
    return 'An error occurred while processing your request. Please try again.';
  }

  return cleanMessage || 'An unexpected error occurred. Please try again.';
};

/**
 * Generates a unique correlation ID for error tracking
 * Uses crypto.randomUUID() for cryptographically secure unique identifiers
 * @returns A unique correlation ID string (UUID v4 format)
 */
export const generateCorrelationId = (): string => {
  // Use crypto.randomUUID() for secure, standardized UUID generation
  // Falls back to a timestamp-based ID if crypto API is unavailable (very rare)
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 8);
  return `${timestamp}-${randomPart}`;
};

/**
 * Logs an error with correlation ID for debugging
 * @param correlationId - The correlation ID for tracking
 * @param error - The error to log
 * @param context - Additional context about where the error occurred
 */
export const logError = (
  correlationId: string,
  error: unknown,
  context?: string,
): void => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;

  console.error(`[${correlationId}]${context ? ` ${context}:` : ''}`, {
    message: errorMessage,
    stack,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Debug logging utility for development environments
 * Only logs in development mode to avoid polluting production logs
 *
 * @param prefix - Log prefix for identification (e.g., '[TenantDetails]')
 * @param message - Log message
 * @param data - Optional data to log
 */
// Helper to check if running in local development
const isLocalDevelopment = (): boolean => {
  if (process.env.NODE_ENV === 'development') return true;
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0'
    );
  }
  return false;
};

export const debugLog = (
  prefix: string,
  message: string,
  data?: unknown,
): void => {
  if (!isLocalDevelopment()) return;

  if (data !== undefined) {
    console.info(`${prefix} ${message}`, data);
  } else {
    console.info(`${prefix} ${message}`);
  }
};

/**
 * Debug warning utility for development environments
 * Only logs warnings in development mode
 *
 * @param prefix - Log prefix for identification
 * @param message - Warning message
 * @param data - Optional data to log
 */
export const debugWarn = (
  prefix: string,
  message: string,
  data?: unknown,
): void => {
  if (!isLocalDevelopment()) return;

  if (data !== undefined) {
    console.warn(`${prefix} ${message}`, data);
  } else {
    console.warn(`${prefix} ${message}`);
  }
};
